import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import axios from 'axios';
import OrderSummary from '../form/orderSummary/wls';
import Modal from '../design/shared/modal';
import { Box } from '../design/css';
import { customStyles, OrderDetailsBox } from '../defectManagement/css';
import { getLocale } from '../misc';
import {
  contentfulFormConfig,
  getContentfulData,
  getData
} from '../contentful';
import {
  keySalesOrderChecklist,
  keyOrderCustomerInformation,
  productValuesToBackendKeys,
  keyWinbackInstallationOrderStep,
  keyOrderSummaryForm,
  keyOrderSummary,
  orderStatus,
  CONSTANT
} from '../utils/constant';
import EN from '../../EN.json';
import FR from '../../FR.json';

import request, { apiHost, config as axiosConfig } from '../utils/request';
import OrderButton from './Actions/components/orderButton';
import ViewOrderHeader from './viewOrderHeader';
import OrderHistory from './Actions/components/orderHistory';
import Comments from './Actions/components/comments';
import OrderAction from './Actions/components/orderAction';
import Attachements from './Actions/components/attachments';

const ViewOrder = ({ orderData, open, setOpen, onStatusUpdateSuccess }) => {
  const locale = getLocale();
  const config = { ...contentfulFormConfig, locale };
  const {
    KEYS: {
      ROLES: { ADMIN }
    }
  } = CONSTANT;

  const {
    WLS: {
      WIRELESS_ORDER_DETAILS: {
        actions: { confirm }
      }
    }
  } = locale === 'en-US' ? EN : FR;

  const { getProfile } = request;
  const profile = getProfile();
  const { role: { name: roleName = 'none' } = {} } = profile || {};

  const [progressBarSteps, setProgressBarSteps] = useState();
  const [comment, setComment] = useState('');
  const [addingComment, setAddingComment] = useState(false);

  const isAdmin = roleName === ADMIN;

  const commentsAllowedStatusesSalesRep = [
    orderStatus.pendingValidation,
    orderStatus.waitingOnSales
  ];

  const commentsAllowedStatusesAdmin = [
    orderStatus.pendingValidation,
    orderStatus.waitingOnSales,
    orderStatus.canceled
  ];

  const shouldShowComments = (isAdmin
    ? commentsAllowedStatusesAdmin
    : commentsAllowedStatusesSalesRep
  ).includes(orderData.status);

  const orderActionsRestrictedStatuses = [
    orderStatus.canceled,
    orderStatus.validated,
    orderStatus.canceledBySalesRep
  ];
  const shouldShowOrderActions =
    !orderActionsRestrictedStatuses.includes(orderData.status) && isAdmin;

  const updateProgressBarSteps = (productSelection, steps) => {
    let result = steps.filter(item =>
      [keySalesOrderChecklist, keyOrderCustomerInformation].includes(
        item.fields.backendKey
      )
    );

    if (productSelection && productSelection.length) {
      result = [
        ...result,
        ...steps.filter(stepItem =>
          productSelection.some(
            item =>
              productValuesToBackendKeys[item] === stepItem.fields.backendKey
          )
        )
      ];
    }

    result = [
      ...result,
      ...steps.filter(item =>
        [
          keyWinbackInstallationOrderStep,
          keyOrderSummaryForm,
          keyOrderSummary
        ].includes(item.fields.backendKey)
      )
    ];

    setProgressBarSteps(steps);
  };

  const onAddComment = () => {
    setAddingComment(true);
    axios
      .post(
        `${apiHost}/orders/${orderData.id}/comments`,
        { text: comment },
        axiosConfig()
      )
      .then(() => {
        onStatusUpdateSuccess();
      })
      .catch(() => {})
      .finally(() => {
        setAddingComment(false);
      });
  };

  const onSaveClick = () => {
    if (comment) onAddComment();
  };

  useEffect(() => {
    getContentfulData(config).then(res => {
      const { formCategories } = getData(res);

      const categoryWireless = formCategories.find(
        category => _.get(category, 'fields.name', '') === 'wireless'
      );
      const wlsOrderSummaryForm = _.get(
        categoryWireless,
        'fields.forms',
        []
      ).find(form => _.get(form, 'fields.name', '') === 'Wireless Online Form');
      updateProgressBarSteps(
        orderData.productSelectionWLS,
        _.get(wlsOrderSummaryForm, 'fields.steps', [])
      );
    });
  }, []);

  return (
    <div id="modal">
      <Modal
        open={open}
        setOpen={setOpen}
        customStyles={customStyles}
        header={orderData.orderId}
      >
        <OrderDetailsBox>
          <ViewOrderHeader orderData={orderData} />
        </OrderDetailsBox>
        <OrderSummary stepData={progressBarSteps} orderData={orderData} />
        <Attachements orderData={orderData} />
        <OrderHistory orderId={orderData.id} />
        {shouldShowComments && (
          <Comments
            comment={comment}
            setComment={setComment}
            orderId={orderData.id}
          />
        )}
        {shouldShowOrderActions && (
          <OrderAction
            orderStatus={orderData.status}
            orderId={orderData.id}
            onSave={onSaveClick}
            onSuccess={onStatusUpdateSuccess}
            comment={comment}
            addingComment={addingComment}
          />
        )}
        {!shouldShowOrderActions && (
          <Box
            mt={20}
            display="flex"
            justifyContent="center"
            width="100"
            style={{ alignItems: 'center' }}
          >
            <Box>
              <OrderButton
                isLoading={addingComment}
                label={confirm}
                disabled={!comment}
                onClick={onAddComment}
                variant="purple"
              />
            </Box>
          </Box>
        )}
        <div style={{ height: 30 }} />
      </Modal>
    </div>
  );
};

ViewOrder.propTypes = {
  orderData: PropTypes.shape({
    orderId: PropTypes.string,
    id: PropTypes.number,
    status: PropTypes.string,
    productSelection: PropTypes.arrayOf(PropTypes.string)
  }).isRequired,
  onStatusUpdateSuccess: PropTypes.func.isRequired
};
export default ViewOrder;
